import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import makeStyles from '@mui/styles/makeStyles';
import { gapi } from "gapi-script";
import googleCalendar from "common/assets/googleCalendar.svg";
import msOutlook from "common/assets/msOutlook.svg";
import yahoo from "common/assets/yahoo.svg";
import apple from "common/assets/apple.svg";
import config from "common/config";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { color, font, radius, type } from "common/tokens";
import { UNLINK, calendarStateFor, clickActionFor, hasExpiredSync } from "business/calendarSync";

const REACT_APP_BASE_URI = config.REACT_APP_BASE_URL;
const redirectUri = `${REACT_APP_BASE_URI}/calendar/callback`;
const OUTLOOK_CLIENT_ID = config.REACT_APP_OUTLOOK_CLIENT_ID;

export const handleGoogleAuth = (saveCalendarIntegration, loadCalendarIntegrations, email) => {
  const CLIENT_ID = config.GOOGLE_CLIENT_ID;
  const SCOPES = "https://www.googleapis.com/auth/calendar.events";

  // Authorization-code flow rather than the token flow: the backend exchanges
  // the code server-side and receives a refresh token, so the calendar keeps
  // working past the 1-hour access token and the nightly job can renew it.
  // The token flow returns no refresh token, so every web-linked calendar
  // lapsed within the hour and could never be auto-renewed (MFD-404).
  const codeClient = window.google.accounts.oauth2.initCodeClient({
    client_id: CLIENT_ID,
    scope: SCOPES,
    ux_mode: "popup",
    callback: async (response) => {
      if (!response.code) {
        return;
      }
      const payload = {
        provider: "google",
        email,
        code: response.code,
        // Google requires 'postmessage' as the redirect_uri when exchanging a
        // code obtained through the popup flow.
        redirect_uri: "postmessage",
      };

      try {
        await saveCalendarIntegration(payload);
      } catch (err) {
        console.error("Failed saving calendar integration:", err);
      }
      // Re-read the list only after the save resolves, otherwise it can return
      // the pre-save record and leave the "sync expired" message up.
      await loadCalendarIntegrations();
    },
  });

  codeClient.requestCode();
};

export const handleOutlookAuth = () => {
  const params = new URLSearchParams({
    client_id: OUTLOOK_CLIENT_ID,
    response_type: "code",
    redirect_uri: redirectUri,
    response_mode: "query",
    scope: "offline_access Calendars.ReadWrite",
    state: "outlook",
  });

  window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
};

export const handleYahooAuth = () => {
  const params = new URLSearchParams({
    client_id:
      "dj0yJmk9Um5HdXJnWUk0YTVjJmQ9WVdrOVJXaDFaVUU1VFVJbWNHbzlNQT09JnM9Y29uc3VtZXJzZWNyZXQmc3Y9MCZ4PTkx",
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile sdct-rw",
    state: "yahoo",
  });

  window.location.href = `https://api.login.yahoo.com/oauth2/request_auth?${params.toString()}`;
};

export const handleAppleICSDownload = () => {
  const icsContent = `
    BEGIN:VCALENDAR
    VERSION:2.0
    BEGIN:VEVENT
    SUMMARY:Mentorship Session
    DTSTART:20250910T100000Z
    DTEND:20250910T110000Z
    DESCRIPTION:Your mentorship session
    END:VEVENT
    END:VCALENDAR
  `;

  const blob = new Blob([icsContent], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "mentorship-session.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    ...type.eyebrow,
    letterSpacing: "1.1px",
    color: color.TEXT_MUTED,
    margin: 0,
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  iconWrapper: {
    position: "relative",
    display: "inline-flex",
    width: 32,
    height: 32,
    appearance: "none",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },
  icon: {
    width: 32,
    height: 32,
    borderRadius: 6,
    display: "block",
  },
  disabledIcon: {
    filter: "grayscale(1)",
    opacity: 0.5,
    cursor: "not-allowed",
  },
  expiredMessage: {
    marginTop: 6,
    fontSize: 12,
    lineHeight: "16px",
    color: color.DANGER,
  },
  statusOverlay: {
    position: "absolute",
    bottom: -2,
    right: -2,
    borderRadius: "50%",
    width: 13,
    height: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    color: color.SURFACE,
    background: color.SUCCESS,
    border: `1.5px solid ${color.SURFACE}`,
  },
  statusOverlayExpired: {
    // Must stay after statusOverlay: JSS emits rules in definition order and
    // both classes apply to the same element, so the later one wins. Defined
    // before it, the green background simply overwrote this (MFD-404).
    background: color.DANGER,
  },
  scrim: {
    position: "fixed",
    inset: 0,
    background: color.SCRIM,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1300,
  },
  dialog: {
    position: "relative",
    background: color.SURFACE,
    borderRadius: radius.MEDIA_CARD,
    padding: "40px 32px 24px",
    width: 320,
    maxWidth: "calc(100% - 32px)",
    boxSizing: "border-box",
  },
  dialogClose: {
    position: "absolute",
    top: 12,
    right: 12,
    appearance: "none",
    background: "none",
    border: "none",
    padding: 4,
    cursor: "pointer",
    display: "flex",
    color: color.TEXT,
  },
  dialogPrompt: {
    ...type.body,
    color: color.TEXT,
    textAlign: "center",
    margin: "0 0 20px",
  },
  dialogConfirm: {
    ...type.meta,
    fontWeight: font.BOLD,
    letterSpacing: "0.4px",
    width: "100%",
    padding: "12px 24px",
    borderRadius: radius.CONTROL,
    border: "none",
    background: color.ACCENT,
    color: color.ON_ACCENT,
    cursor: "pointer",
  },
});

const CalendarIntegrationOptions = ({
  saveCalendarIntegration,
  unlinkCalendarIntegration,
  calendarIntegrations,
  loadCalendarIntegrations,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const email = useSelector((state) => state.auth.user.email);

  const [calendars, setCalendars] = useState([]);
  const [pendingUnlink, setPendingUnlink] = useState(null);

  useEffect(() => {
    function start() {
      gapi.client.init({
        discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest"],
      });
    }
    gapi.load("client", start);
  }, []);

  const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

  const confirmUnlink = () => {
    const provider = pendingUnlink;
    setPendingUnlink(null);
    unlinkCalendarIntegration({ provider, email })
      .then(() => loadCalendarIntegrations())
      .catch((err) => console.error(`Failed to unlink ${provider}:`, err));
  };

  useEffect(() => {
    const calendarsData = [
      {
        name: "Google Calendar",
        icon: googleCalendar,
        onClick: () => handleGoogleAuth(saveCalendarIntegration, loadCalendarIntegrations, email),
        provider: "google",
        ...calendarStateFor(calendarIntegrations, "google"),
      },
      {
        name: "Outlook",
        icon: msOutlook,
        onClick: handleOutlookAuth,
        provider: "outlook",
        ...calendarStateFor(calendarIntegrations, "outlook"),
      },
      {
        name: "Yahoo",
        icon: yahoo,
        onClick: handleYahooAuth,
        provider: "yahoo",
        connected: calendarIntegrations?.some((ci) => ci.provider === "yahoo"),
      },
      {
      name: "Apple",
      icon: apple,
      onClick: handleAppleICSDownload,
      provider: "apple",
      connected: calendarIntegrations?.some((ci) => ci.provider === "apple"),
    },
    ];
    setCalendars(calendarsData);
  }, [email, calendarIntegrations, saveCalendarIntegration, loadCalendarIntegrations]);

  const hasAnyConnected = calendars.some((c) => c.connected);
  const hasExpiredIntegration = hasExpiredSync(calendars);

  return (
    <div className={classes.wrapper}>
      <p className={classes.label}>{t("syncToYourCalendar")}</p>

      <div className={classes.icons}>
        {calendars.map((cal) => {
          const isDisabled = hasAnyConnected && !cal.connected;
          const isExpired = cal.connected && cal.needsReconnect;

          return (
            <button
              type={"button"}
              key={cal.provider}
              className={classes.iconWrapper}
              title={isExpired ? t("calendarSyncExpiredTitle") : cal.name}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return;
                // An expired calendar reconnects rather than unlinks; sending
                // it to the unlink dialog is why "click to reconnect" could
                // never work.
                if (clickActionFor(cal) === UNLINK) {
                  setPendingUnlink(cal.provider);
                } else if (cal.onClick) {
                  cal.onClick();
                }
              }}
            >
              <img
                src={cal.icon}
                alt={cal.name}
                className={`${classes.icon} ${isDisabled ? classes.disabledIcon : ""}`}
              />
              {cal.connected && (
                <span
                  className={`${classes.statusOverlay} ${isExpired ? classes.statusOverlayExpired : ""}`}
                >
                  {isExpired ? "!" : "✔"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {hasExpiredIntegration && (
        <p className={classes.expiredMessage}>{t("calendarSyncExpired")}</p>
      )}

      {pendingUnlink && (
        <div className={classes.scrim} role={"presentation"} onClick={() => setPendingUnlink(null)}>
          <div className={classes.dialog} role={"dialog"} onClick={(e) => e.stopPropagation()}>
            <button
              type={"button"}
              className={classes.dialogClose}
              aria-label={t("cancelLabel")}
              onClick={() => setPendingUnlink(null)}
            >
              <X size={18} strokeWidth={2} />
            </button>
            <p className={classes.dialogPrompt}>
              {t("unlinkCalendarConfirm", { provider: capitalize(pendingUnlink) })}
            </p>
            <button type={"button"} className={classes.dialogConfirm} onClick={confirmUnlink}>
              {t("confirmCaps")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarIntegrationOptions;
